import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { User, Mail, Lock, Phone, MapPin, LogIn, UserPlus, RefreshCw, Users, Heart, AlertCircle } from 'lucide-react';

const volunteerInterests = [
  'Community Outreach',
  'Event Planning',
  'Youth Programs',
  'Senior Support',
  'Environmental Projects',
  'Neighborhood Watch',
  'Transportation Assistance',
  'Administrative Support'
];

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [userType, setUserType] = useState('member');

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    address: '',
    user_type: 'member',
    skills: '',
    availability: '',
    interests: []
  });

  const from = location.state?.from?.pathname || '/';

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const result = await login(loginData.email, loginData.password);
      
      // Check if user is pending
      if (result.user?.status === 'pending') {
        toast.info('Your account is pending approval. You can browse the site while waiting.', { duration: 6000 });
      } else {
        toast.success('Welcome back!');
      }
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.email || !registerData.password || !registerData.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (userType === 'volunteer' && !registerData.phone) {
      toast.error('Phone number is required for volunteer registration');
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        ...registerData,
        user_type: userType,
        skills: registerData.skills ? registerData.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        interests: registerData.interests
      };
      
      await register(dataToSend);
      toast.success(
        'Account created! Your registration is pending admin approval. You will be notified once approved.',
        { duration: 8000 }
      );
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleInterest = (interest) => {
    setRegisterData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-plp-blue rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-heading font-bold text-2xl">KC</span>
          </div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Killarney Connect</h1>
          <p className="text-slate-600">Sign in to participate in your community</p>
        </div>

        <Card className="bg-white shadow-lg" data-testid="auth-card">
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Sign Up
                </TabsTrigger>
              </TabsList>

              {/* Login Form */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your@email.com"
                        className="pl-10"
                        value={loginData.email}
                        onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={loginData.password}
                        onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                        data-testid="login-password-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-plp-blue hover:bg-plp-blue-light"
                    data-testid="login-submit"
                  >
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                    Sign In
                  </Button>
                </form>
              </TabsContent>

              {/* Register Form */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  {/* User Type Selection */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button
                      type="button"
                      onClick={() => setUserType('member')}
                      data-testid="user-type-member"
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        userType === 'member'
                          ? 'border-plp-blue bg-plp-blue/5'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <User className={`w-6 h-6 mb-2 ${userType === 'member' ? 'text-plp-blue' : 'text-slate-400'}`} />
                      <p className="font-medium text-sm">Community Member</p>
                      <p className="text-xs text-slate-500">Join the community</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setUserType('volunteer')}
                      data-testid="user-type-volunteer"
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        userType === 'volunteer'
                          ? 'border-plp-gold bg-plp-gold/5'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Heart className={`w-6 h-6 mb-2 ${userType === 'volunteer' ? 'text-plp-gold' : 'text-slate-400'}`} />
                      <p className="font-medium text-sm">Volunteer</p>
                      <p className="text-xs text-slate-500">Help the community</p>
                    </button>
                  </div>

                  <div>
                    <Label htmlFor="register-name">Full Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-name"
                        placeholder="Your name"
                        className="pl-10"
                        value={registerData.name}
                        onChange={(e) => setRegisterData(prev => ({ ...prev, name: e.target.value }))}
                        data-testid="register-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="register-email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="your@email.com"
                        className="pl-10"
                        value={registerData.email}
                        onChange={(e) => setRegisterData(prev => ({ ...prev, email: e.target.value }))}
                        data-testid="register-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="register-password">Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10"
                        value={registerData.password}
                        onChange={(e) => setRegisterData(prev => ({ ...prev, password: e.target.value }))}
                        data-testid="register-password-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="register-phone">Phone {userType === 'volunteer' && '*'}</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-phone"
                        placeholder="242-XXX-XXXX"
                        className="pl-10"
                        value={registerData.phone}
                        onChange={(e) => setRegisterData(prev => ({ ...prev, phone: e.target.value }))}
                        data-testid="register-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="register-address">Address in Killarney</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-address"
                        placeholder="Your address"
                        className="pl-10"
                        value={registerData.address}
                        onChange={(e) => setRegisterData(prev => ({ ...prev, address: e.target.value }))}
                        data-testid="register-address-input"
                      />
                    </div>
                  </div>

                  {/* Volunteer-specific fields */}
                  {userType === 'volunteer' && (
                    <div className="space-y-4 pt-2 border-t">
                      <div className="flex items-center gap-2 text-plp-gold">
                        <Users className="w-4 h-4" />
                        <span className="text-sm font-medium">Volunteer Information</span>
                      </div>
                      
                      <div>
                        <Label>Skills (comma separated)</Label>
                        <Input
                          placeholder="e.g., Event planning, First aid, Teaching"
                          value={registerData.skills}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, skills: e.target.value }))}
                          data-testid="register-skills-input"
                        />
                      </div>

                      <div>
                        <Label>Availability</Label>
                        <Input
                          placeholder="e.g., Weekends, Evenings, Flexible"
                          value={registerData.availability}
                          onChange={(e) => setRegisterData(prev => ({ ...prev, availability: e.target.value }))}
                          data-testid="register-availability-input"
                        />
                      </div>

                      <div>
                        <Label className="mb-2 block">Areas of Interest</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {volunteerInterests.map((interest) => (
                            <label
                              key={interest}
                              className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                              <Checkbox
                                checked={registerData.interests.includes(interest)}
                                onCheckedChange={() => toggleInterest(interest)}
                                className="flex-shrink-0"
                              />
                              <span className="text-slate-600 leading-tight">{interest}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pending Approval Notice */}
                  <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Account Approval Required</p>
                      <p className="text-amber-700 text-xs mt-0.5">
                        Your registration will be reviewed by our team to ensure community safety.
                        You'll be notified once approved.
                      </p>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className={`w-full ${userType === 'volunteer' ? 'bg-plp-gold hover:bg-plp-gold-light text-plp-blue' : 'bg-plp-blue hover:bg-plp-blue-light'}`}
                    data-testid="register-submit"
                  >
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                    {userType === 'volunteer' ? 'Register as Volunteer' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          By signing in, you agree to help build a better Killarney community.
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
